import {
  SeriesAreaDataOptions,
  SeriesAreaOptions,
  SeriesLineOptions,
  SeriesXrangeOptions
} from "highcharts";
import { addMinutes, setHours } from "date-fns";
import * as yup from "yup";
import {
  ChartFieldsFragment,
  InsightChartMeta,
  InsightSeriesMetaFieldsFragment,
  LensTheme,
  Point,
  TimeScope
} from "generated/graphql";
import { NonNullableRequired } from "./types";
import i18next from "utilities/i18n";
import { handleNullFields } from "./stringUtils";
import { COLORS } from "Constants";

// color names derived with this tool: http://chir.ag/projects/name-that-color
export const CHART_COLORS = {
  PATTENS: "#E0F6FF",
  DAINTREE: "#00263e",
  FUSCHIA: "#b366af",
  PUERTO: "#4ac6b0",
  NAVAJO: "#FFdeAC",
  MONA_LISA: "#FF9597"
};

export const determineChartType = (
  meta?: InsightChartMeta
): "area" | "xrange" => {
  if (!meta || !meta?.timeScope) {
    return "area";
  }

  const timeScopes = [TimeScope.Day];

  const xrangeTopics = [
    "room usage",
    "unscheduled room usage",
    "meetings that start late",
    "meetings that run long",
    "rooms scheduled but not used",
    "average meeting size",
    "recurring meetings with no participants",
    "offline devices",
    "call usage"
  ];

  if (
    timeScopes.includes(meta.timeScope) &&
    xrangeTopics.includes(meta.topic)
  ) {
    return "xrange";
  }

  return "area";
};

export const colorsForChart = (
  type: "area" | "xrange",
  seriesLength: number | undefined,
  theme: LensTheme | "LIGHT"
): Array<string> => {
  if (!seriesLength) {
    return [];
  }

  if (type === "xrange") {
    return [
      CHART_COLORS.PUERTO,
      CHART_COLORS.NAVAJO,
      CHART_COLORS.MONA_LISA,
      CHART_COLORS.FUSCHIA
    ];
  }

  const colors = [
    CHART_COLORS.FUSCHIA,
    CHART_COLORS.PUERTO,
    CHART_COLORS.NAVAJO,
    CHART_COLORS.MONA_LISA
  ];

  return colors.slice(0, seriesLength);
};

/**
 * Creates a type based on Series in which Series and Series Data are not nullable.
 */
export type NotNullableSeries = NonNullableRequired<
  ChartFieldsFragment["series"]
>;

/**
 * Charts expect all series / data to not be null.
 * Series / Data from schema have nulls.
 * Filter out the null data values to make the charts happy.
 *
 * @param {ChartFieldsFragment["series"]} series
 *
 * @return {NotNullableSeries}
 */
const deNullifySeries = (series?: ChartFieldsFragment["series"]) => {
  return series
    ? (series.filter(s => null !== s.data) as NotNullableSeries)
    : [];
};

/**
 * Construct a name for each series based on its locationScope and meta
 *
 * @param {SeriesMetaFieldsFragment} meta
 *
 * @return {string}
 */

const findName = (meta: InsightSeriesMetaFieldsFragment) => {
  let name;
  const scope = i18next.t(meta.locationScope);
  switch (meta.object?.__typename) {
    case "Device":
      name = meta.object.deviceName;
      break;
    case "Room":
      name = meta.object.roomName;
      break;
    case "Site":
      name = meta.object.siteName;
      break;
    case "Tenant":
      name = meta.object.tenantName;
      break;
    default:
      name = handleNullFields(meta.object?.id);
  }
  return i18next.t("INSIGHT_CHART_SERIES_NAME", {
    scope: scope,
    name: handleNullFields(name)
  });
};

/**
 * Transform the data from graphQL to a useful structure for the Area chart.
 *
 * @param {ChartFieldsFragment["series"] | undefined} series
 * @param {LensTheme | "LIGHT"} theme
 *
 * @return {Array<SeriesAreaOptions>}
 */
export const mapAreaChartSeries = (
  series: ChartFieldsFragment["series"] | undefined,
  theme: LensTheme | "LIGHT"
): Array<SeriesAreaOptions> => {
  return deNullifySeries(series)
    .filter(s => s.meta.__typename === "InsightSeriesMeta")
    .map((s, i) => {
      const data = s.data.map((point: Point) => {
        return {
          ...point,
          x: new Date(point.x).valueOf(),
          y: point.y
        };
      });

      return {
        ...s,
        name: findName(s.meta as InsightSeriesMetaFieldsFragment),
        color: i
          ? undefined
          : theme === LensTheme.Light
          ? CHART_COLORS.DAINTREE
          : CHART_COLORS.PATTENS,
        type: i ? "area" : "line",
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: !i
            }
          }
        },
        zIndex: i ? 8 : 40,
        data
      } as SeriesAreaOptions;
    });
};

/**
 * Transform the data from graphQL to a useful structure for the XRange chart.
 *
 * @param {ChartFieldsFragment["series"] | undefined} series
 *
 * @return {Array<SeriesXrangeOptions>}
 */
export const mapXRangeChartSeries = (
  series: ChartFieldsFragment["series"] | undefined
): Array<SeriesXrangeOptions> => {
  return deNullifySeries(series).map((s, y) => {
    const data = s.data.map((point: Point) => {
      const start = point && point.x ? point.x : 0;
      const end = point && point.y ? point.y : 0;
      const startDate = new Date(start);
      const endDate = addMinutes(startDate, end);

      return {
        ...point,
        x: startDate.getTime(),
        x2: endDate.getTime(),
        y
      };
    });

    return {
      ...s,
      type: "xrange",
      data,
      name: "",
      maxPointWidth: 32,
      borderWidth: 0,
      dataLabels: {
        enabled: true
      }
    } as SeriesXrangeOptions;
  });
};

/**
 * For an XRange Chart, the minimum / maximum times are necessary.
 * Rather than hard-code fixed times, this function attempts to
 * determine the range from the passed-in data.
 *
 * @param {Array<SeriesXrangeOptions>} series
 */
export const calcXRangeAxisMaxAndMin = (series: Array<SeriesXrangeOptions>) => {
  const hugeYear = new Date().getFullYear() + 100;
  const hugeDate = Date.UTC(hugeYear, 12, 31);

  const min = series.reduce((totalMin: number, series) => {
    if (!series || !series.data) {
      return totalMin;
    }

    const seriesMin = series.data.reduce((min: number, point) => {
      if (!point || !point.x) {
        return min;
      }

      return min < +point.x ? min : +point.x;
    }, hugeDate);
    return seriesMin < totalMin ? seriesMin : totalMin;
  }, hugeDate);

  const max = series.reduce((totalMax: number, series) => {
    if (!series || !series.data) {
      return totalMax;
    }

    const seriesMax = series.data.reduce((max: number, point) => {
      if (!point || !point.x2) {
        return max;
      }

      return max > +point.x2 ? max : +point.x2;
    }, 0);
    return seriesMax > totalMax ? seriesMax : totalMax;
  }, 0);

  const minDate = new Date(min);
  const maxDate = new Date(max);

  const xMin = setHours(minDate, 0).getTime();
  const xMax = setHours(maxDate, 24).getTime();

  return { min: xMin, max: xMax };
};

export type SeriesComboOptions = SeriesAreaOptions | SeriesLineOptions;

export type SeriesComboChartTypes = "area" | "line";

/**
 * Update chart series to specify their type in the series data
 *
 * @param {SeriesComboOptions[]} series series to update
 * @param {SeriesComboChartTypes} chartType type to add to series data
 * @returns {SeriesComboOptions[]} updated series with type added
 */
export const addTypeToComboChartSeries = (
  series: SeriesComboOptions[],
  chartType: SeriesComboChartTypes
): SeriesComboOptions[] => {
  return series.map(seriesElement => {
    seriesElement.type = chartType;
    return seriesElement;
  });
};

/**
 * Creates a horizontal line series at a given threshold value.
 *
 * @param {number[]} areaRange Range of X-axis values to use in the horizontal line series
 * @param {number} threshold The threshold to draw a horizontal line at
 * @param seriesOptions Object that contains various options for the new series
 * @returns {SeriesLineOptions} The Horizontal Line Series
 */
export const createHorizontalThresholdSeries = (
  areaRange: number[],
  threshold: number,
  seriesOptions: {
    seriesName: string;
    seriesColor: string;
  }
): SeriesLineOptions => {
  const data = areaRange.map(num => ({
    x: num,
    y: threshold
  }));

  const defaultOptions = {
    seriesName: "",
    seriesColor: COLORS.THRESHOLD_RED
  };

  const options = { ...defaultOptions, ...seriesOptions };

  return {
    name: options.seriesName,
    type: "line",
    data: data,
    color: options.seriesColor,
    marker: {
      enabled: false,
      states: {
        hover: {
          enabled: false
        }
      }
    },
    dashStyle: "Dot",
    zIndex: 120,
    opacity: 0.9,
    description: options.seriesName,
    enableMouseTracking: true
  };
};

/**
 * build list of all x-axis values for data-points in the area series
 *
 * @param {SeriesAreaOptions[]} series The array of area series
 * @returns {number[]} list of x-axis values
 */
export const getRangeFromAreaSeries: (
  series: SeriesAreaOptions[]
) => number[] = (series: SeriesAreaOptions[]) => {
  // Using a yup schema to narrow down typing for the 'data' property within an array of area series.
  // It has a lot of variations that we don't care about. We want the version that uses an array of
  // SeriesAreaDataOptions objects and that have the 'x' and 'y' properties.
  const yupSchemaSeriesAreaDataOptions = yup.array().of(
    yup.object<Pick<SeriesAreaDataOptions, "x" | "y">>({
      x: yup.number(),
      y: yup.number().nullable()
    })
  );
  // get a list of all x-axis values being used in the area chart series
  const allRanges = series.flatMap(seriesElement => {
    const validatedSeriesData = yupSchemaSeriesAreaDataOptions.validateSync(
      seriesElement.data
    );
    // Pull out only x values that are defined. Skip undefined
    return validatedSeriesData
      .map(e => e.x)
      .filter((x): x is number => Boolean(x));
  });
  return allRanges;
};
